import { JSONContent } from '@tiptap/core';
import { RdsDropdownDirective } from '@rds/angular-components';

import { chipDataModel } from '../model/filter.model';

/**
 * Recursively extracts plain text from a TipTap JSONContent tree.
 * Also handles legacy HTML strings gracefully.
 */
export function extractTextFromJsonContent(json: JSONContent | string): string {
    if (!json) return '';
    if (typeof json === 'string') return json;
    let text = '';
    if (json.text) {
        text += json.text;
    }
    if (json.content) {
        for (const child of json.content) {
            text += extractTextFromJsonContent(child);
        }
    }
    return text;
}

/**
 * Determines whether a TipTap block node represents an "empty line", i.e. a
 * paragraph the user left blank (no text or only whitespace) with no meaningful
 * inline content such as an equation or hard break.
 */
function isEmptyLineNode(node: JSONContent): boolean {
    if (!node || node.type !== 'paragraph') {
        return false;
    }
    if (!node.content || node.content.length === 0) {
        return true;
    }
    const hasMeaningfulContent = node.content.some((child) => {
        if (child.type === 'text') {
            return (child.text ?? '').trim().length > 0;
        }
        // Any non-text inline node (hardBreak, mathInline, etc.) is meaningful.
        return true;
    });
    return !hasMeaningfulContent;
}

/**
 * Removes empty lines (blank paragraphs) from a TipTap JSONContent document
 * before it is sent to the API. Trailing/leading and in-between blank
 * paragraphs the user created are dropped. Non-paragraph blocks (lists, etc.)
 * are left untouched. Falls back to a single empty paragraph when everything is
 * removed so the editor still has a valid document.
 */
export function trimEmptyLinesFromJsonContent(
    value: JSONContent | string
): JSONContent | string {
    if (typeof value === 'string') {
        return value;
    }
    if (!value || value.type !== 'doc' || !Array.isArray(value.content)) {
        return value;
    }

    const trimmedContent = value.content.filter(
        (node) => !isEmptyLineNode(node)
    );

    return {
        ...value,
        content:
            trimmedContent.length > 0
                ? trimmedContent
                : [{ type: 'paragraph' }],
    };
}

/**
 * Normalizes criteria_text from the API response.
 * If the value is already a JSONContent object, returns it as-is.
 * If it's an HTML string (old format), parses it into TipTap JSONContent
 * so the mini-editor always receives a JSONContent object.
 */
export function normalizeCriteriaText(
    value: JSONContent | string
): JSONContent {
    if (!value) {
        return { type: 'doc', content: [] };
    }
    if (typeof value === 'object' && value.type) {
        return value;
    }
    if (typeof value === 'string') {
        return parseHtmlToJsonContent(value);
    }
    return { type: 'doc', content: [] };
}

/**
 * Converts an HTML string into TipTap-compatible JSONContent using the
 * browser's DOMParser. Supports: p, strong/b, em/i, u, sub, sup, ul, ol, li.
 */
function parseHtmlToJsonContent(html: string): JSONContent {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;

    const children = convertChildNodes(body);
    if (children.length === 0) {
        const text = body.textContent?.trim();
        if (text) {
            return {
                type: 'doc',
                content: [
                    { type: 'paragraph', content: [{ type: 'text', text }] },
                ],
            };
        }
        return { type: 'doc', content: [] };
    }
    return { type: 'doc', content: children };
}

function convertChildNodes(parent: Node): JSONContent[] {
    const result: JSONContent[] = [];
    parent.childNodes.forEach((node) => {
        const converted = convertNode(node);
        if (converted) {
            if (Array.isArray(converted)) {
                result.push(...converted);
            } else {
                result.push(converted);
            }
        }
    });
    return result;
}

function convertNode(node: Node): JSONContent | JSONContent[] | null {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (!text) return null;
        return { type: 'text', text };
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    switch (tag) {
        case 'p': {
            const content = convertInlineChildren(el);
            return {
                type: 'paragraph',
                ...(content.length > 0 ? { content } : {}),
            };
        }
        case 'ul': {
            const items = convertListItems(el);
            return {
                type: 'bulletList',
                ...(items.length > 0 ? { content: items } : {}),
            };
        }
        case 'ol': {
            const items = convertListItems(el);
            return {
                type: 'orderedList',
                ...(items.length > 0 ? { content: items } : {}),
            };
        }
        case 'li':
            return convertListItem(el);
        case 'br':
            return { type: 'hardBreak' };
        default: {
            const children = convertChildNodes(el);
            if (children.length > 0) return children;
            return null;
        }
    }
}

function convertListItems(listEl: HTMLElement): JSONContent[] {
    const items: JSONContent[] = [];
    listEl.childNodes.forEach((child) => {
        if (
            child.nodeType === Node.ELEMENT_NODE &&
            (child as HTMLElement).tagName.toLowerCase() === 'li'
        ) {
            items.push(convertListItem(child as HTMLElement));
        }
    });
    return items;
}

/**
 * Convert an `<li>` to a TipTap listItem. The item's inline text becomes a
 * leading paragraph; any nested `<ul>`/`<ol>` are converted to their own list
 * nodes and appended as siblings of that paragraph, so nested bullet levels are
 * preserved instead of dropped (which previously trimmed criterion sub-items).
 */
function convertListItem(el: HTMLElement): JSONContent {
    const inline = convertInlineChildren(el);
    const nestedLists: JSONContent[] = [];
    el.childNodes.forEach((child) => {
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const tag = (child as HTMLElement).tagName.toLowerCase();
        if (tag === 'ul' || tag === 'ol') {
            const converted = convertNode(child);
            if (Array.isArray(converted)) {
                nestedLists.push(...converted);
            } else if (converted) {
                nestedLists.push(converted);
            }
        }
    });

    return {
        type: 'listItem',
        content: [
            {
                type: 'paragraph',
                ...(inline.length > 0 ? { content: inline } : {}),
            },
            ...nestedLists,
        ],
    };
}

function convertInlineChildren(parent: HTMLElement): JSONContent[] {
    const result: JSONContent[] = [];

    parent.childNodes.forEach((node) => {
        // Route A: Parse direct leaf text nodes cleanly
        if (node.nodeType === Node.TEXT_NODE) {
            parseTextNode(node, result);
            return;
        }

        // Route B: Parse element wrappers and look for nested nodes
        if (node.nodeType === Node.ELEMENT_NODE) {
            parseElementNode(node as HTMLElement, result);
        }
    });

    return result;
}

// =========================================================================
// Extracted Single-Responsibility Sub-Parsers
// =========================================================================

function parseTextNode(node: ChildNode, result: JSONContent[]): void {
    const text = node.textContent || '';
    if (text) {
        result.push({ type: 'text', text });
    }
}

function parseElementNode(el: HTMLElement, result: JSONContent[]): void {
    const tag = el.tagName.toLowerCase();

    // Guard Clause: Instantly drop block structural lists out of inline contexts
    if (tag === 'ul' || tag === 'ol') return;

    if (tag === 'br') {
        result.push({ type: 'hardBreak' });
        return;
    }

    const mark = getMarkForTag(tag, el);
    const children = convertInlineChildren(el);

    if (children.length === 0) {
        buildLeafTextWithMark(el, mark, result);
        return;
    }

    appendMarksToInlineChildren(children, mark, result);
}

function buildLeafTextWithMark(
    el: HTMLElement,
    mark: any,
    result: JSONContent[]
): void {
    const text = el.textContent || '';
    if (!text) return;

    const textNode: JSONContent = { type: 'text', text };
    if (mark) {
        textNode.marks = [mark];
    }
    result.push(textNode);
}

function appendMarksToInlineChildren(
    children: JSONContent[],
    mark: any,
    result: JSONContent[]
): void {
    for (const child of children) {
        // Only apply style text decorations onto valid text nodes natively
        if (child.type === 'text' && mark) {
            child.marks = [...(child.marks || []), mark];
        }
        result.push(child);
    }
}

function getMarkForTag(
    tag: string,
    el: HTMLElement
): { type: string; attrs?: Record<string, any> } | null {
    switch (tag) {
        case 'strong':
        case 'b':
            return { type: 'bold' };
        case 'em':
        case 'i':
            return { type: 'italic' };
        case 'u':
            return { type: 'underline' };
        case 'sub':
            return { type: 'subscript' };
        case 'sup':
            return { type: 'superscript' };
        case 'span': {
            const color = el.style.color;
            if (color) return { type: 'textColor', attrs: { color } };
            return null;
        }
        case 'mark': {
            const bg = el.style.backgroundColor;
            if (bg) return { type: 'highlight', attrs: { color: bg } };
            return null;
        }
        default:
            return null;
    }
}

export async function sha256(message: string) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    return hashHex;
}

export function toTitleCase(input: string): string {
    if (!input) return '';
    input = input.replace('_', ' ');
    return input
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function createChipObject(
    objectType: string,
    valueList: string[],
    objectDataType: string = 'string',
    objectTitle: string = ''
) {
    return {
        filter: objectType,
        title: objectTitle || toTitleCase(objectType),
        type: objectDataType,
        value: valueList,
    };
}

export function addKeytoChipbyObjectTypeValueArray(
    chipsObject: Record<string, any>,
    objectType: string,
    valueList: string[],
    objectTitle: string = '',
    objectDataType: string = 'string'
): void {
    chipsObject[objectType] = createChipObject(
        objectType,
        valueList,
        objectDataType,
        objectTitle
    );
}

export function addKeytoChipbyObjectType(
    chipsObject: Record<string, any>,
    objectType: string,
    valueList: string[],
    objectDataType: string = 'string',
    additionalProperties: Record<string, any> = {}
): void {
    chipsObject[objectType] = {
        ...createChipObject(objectType, valueList, objectDataType),
        ...additionalProperties,
    };
}

export function deleteObjectFromChipByObjectType(
    chipsObject: Record<string, any>,
    objectType: string
): void {
    delete chipsObject[objectType];
}

export function extractAvailableIndications(tableData: any): string[] {
    const resultArray: string[] = [];
    const existingIndications = tableData
        .map((element: any) => element.indication)
        .sort();

    for (const indication of existingIndications) {
        const parts = indication.split('/').map((part: string) => part.trim());
        let segment = '';

        for (const part of parts) {
            segment = segment ? `${segment}/${part}` : part;

            if (!resultArray.includes(segment)) {
                resultArray.push(segment);
            }
        }
    }

    return resultArray;
}

export function sortArray(array: string[], type: string): string[] {
    let sortedArray: string[] = [];
    switch (type) {
        case 'normal':
            array.sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' })
            );
            break;
        case 'undefined':
            array.sort((a = '', b = '') => {
                const a_assigned: string = a;
                const b_assigned: string = b;
                return a_assigned.localeCompare(b_assigned, undefined, {
                    sensitivity: 'base',
                });
            });
            break;
        case 'date':
            array.sort((a, b) => {
                const a_date: number = new Date(a).valueOf();
                const b_date: number = new Date(b).valueOf();
                return a_date - b_date;
            });

            break;
    }
    sortedArray = array;
    return sortedArray;
}

export function isSubString(str: string, subStr: string) {
    return str
        .toLowerCase()
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&ndash;', '-')
        .replaceAll('&mdash;', '_')
        .includes(subStr.toLowerCase());
}

export function hideShowDropdownElement(
    dropdownElement: RdsDropdownDirective,
    isOpen: boolean
) {
    switch (true) {
        case isOpen:
            dropdownElement.show();
            break;
        case !isOpen:
            dropdownElement.hide();
            break;
    }
}

export function isUrlValid(url: string) {
    const regex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,4}(\/[\w-]*)*\/?$/;
    return regex.test(url);
}

export function isValueFiltered(
    filterValues: any[],
    value: any,
    ValueFunc?: string,
    dataType: string = 'string'
): boolean {
    if (dataType === 'string') {
        if (filterValues.length === 0) {
            return false; // No filter values, no need to filter
        }
        const valueToCheck = ValueFunc ?? value;
        return !filterValues.includes(valueToCheck); // Check if value is NOT in the filter
    } else {
        if (filterValues[0] === -1) {
            return false;
        }
        const dateValue = new Date(value).valueOf();
        return dateValue < filterValues[0] || dateValue > filterValues[1];
    }
}

/**
 * Serialise a payload object into a query string.
 *
 * Arrays are appended one element per key (`ids=a&ids=b`), which is the
 * repeated-key form FastAPI decodes into a list. Handing the object straight to
 * `URLSearchParams` instead comma-joins an array into a single value
 * (`ids=a%2Cb`), which the backend then reads as one malformed entry.
 * `null`/`undefined` values are skipped rather than serialised as the strings
 * "null"/"undefined".
 */
export function buildQueryString(params: object): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
        if (value === null || value === undefined) continue;
        if (Array.isArray(value)) {
            for (const entry of value) {
                if (entry === null || entry === undefined) continue;
                search.append(key, String(entry));
            }
            continue;
        }
        search.append(key, String(value));
    }
    return search.toString();
}

export function calculateTotalSelectedFilterValues(
    chips: chipDataModel[]
): number {
    return chips.reduce((total, chip) => {
        if (chip.type === 'date') {
            return total + 2;
        }
        return total + (Array.isArray(chip.value) ? chip.value.length : 0);
    }, 0);
}
